/**
 * The Settings dialog's shape, as data: groups (the nav's headings) hold
 * pages (nav items, one `?settings=<id>` each), pages hold headed sections,
 * sections hold settings. Kept free of React so the search below and the
 * "which pages are empty" rule can be tested on their own; the controls
 * are attached by id in `settings-registry.tsx`.
 */
export interface SettingEntry<Text extends string = string> {
	/** Also the DOM id suffix search scrolls to (`settings-row-<id>`). */
	id: string;
	label: Text;
	description?: Text;
	/** Extra words search should match ("dark" → Theme). */
	keywords?: ReadonlyArray<string>;
	/** `stacked`: the control sits under the label, full width. */
	layout?: 'inline' | 'stacked';
	/**
	 * The label is not shown: for the one setting of a section whose title
	 * already says it. It still names the control for a screen reader and
	 * for search, and the description is shown as usual.
	 */
	hideLabel?: boolean;
}

export interface SettingsSection<Text extends string = string> {
	id: string;
	title: Text;
	/**
	 * The title is not shown: for a page with one section named like the
	 * page. It still names the section for a screen reader and for search.
	 */
	hideTitle?: boolean;
	settings: ReadonlyArray<SettingEntry<Text>>;
}

export interface SettingsPage<Text extends string = string> {
	id: string;
	label: Text;
	sections: ReadonlyArray<SettingsSection<Text>>;
}

/**
 * `Text` is `MessageKey` in the registry (so a plain English string there is
 * a type error) and the translated `string` once localized.
 */
export interface SettingsGroup<Text extends string = string> {
	id: string;
	label: Text;
	pages: ReadonlyArray<SettingsPage<Text>>;
}

/**
 * Drops settings this tenant doesn't offer, then any section and page left
 * empty -- so a page never shows up with nothing on it, and the dialog
 * opens on the first page that has something.
 */
export function withAvailableSettings(
	groups: ReadonlyArray<SettingsGroup>,
	isAvailable: (settingId: string) => boolean,
): Array<SettingsGroup> {
	return groups
		.map((group) => ({
			...group,
			pages: group.pages
				.map((page) => ({
					...page,
					sections: page.sections
						.map((section) => ({
							...section,
							settings: section.settings.filter((s) =>
								isAvailable(s.id),
							),
						}))
						.filter((section) => section.settings.length),
				}))
				.filter((page) => page.sections.length),
		}))
		.filter((group) => group.pages.length);
}

export interface SettingsSearchResult {
	page: SettingsPage;
	section: SettingsSection;
	setting: SettingEntry;
}

/**
 * Every setting whose label, description, keywords, section title or page
 * name contains every word of `query` (case-insensitive) -- so "general"
 * lists everything on General, and "dark mode" finds Theme.
 */
export function searchSettings(
	groups: ReadonlyArray<SettingsGroup>,
	query: string,
): Array<SettingsSearchResult> {
	const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
	if (!terms.length) {
		return [];
	}
	const results: Array<SettingsSearchResult> = [];
	for (const group of groups) {
		for (const page of group.pages) {
			for (const section of page.sections) {
				for (const setting of section.settings) {
					const haystack = [
						setting.label,
						setting.description ?? '',
						...(setting.keywords ?? []),
						section.title,
						page.label,
					]
						.join(' ')
						.toLowerCase();
					if (terms.every((term) => haystack.includes(term))) {
						results.push({ page, section, setting });
					}
				}
			}
		}
	}
	return results;
}

/**
 * The registry holds message keys (`settings.page.general`); this swaps in
 * their text in the app language, before search and render, so search
 * matches what the reader reads.
 */
export function localizeSettings(
	groups: ReadonlyArray<SettingsGroup>,
	translate: (key: string) => string,
): Array<SettingsGroup> {
	return groups.map((group) => ({
		...group,
		label: translate(group.label),
		pages: group.pages.map((page) => ({
			...page,
			label: translate(page.label),
			sections: page.sections.map((section) => ({
				...section,
				title: translate(section.title),
				settings: section.settings.map((setting) => ({
					...setting,
					label: translate(setting.label),
					description: setting.description
						? translate(setting.description)
						: undefined,
				})),
			})),
		})),
	}));
}

export function findPage(
	groups: ReadonlyArray<SettingsGroup>,
	pageId: string | null,
): SettingsPage | undefined {
	return groups
		.flatMap((group) => group.pages)
		.find((page) => page.id === pageId);
}
